'use client'

import {
  Component,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore
} from 'react'

import { isClubUserSession } from '@/features/tasks/fanTasksAccess'
import { usePathname } from '@/i18n/routing'
import { useModalStore } from '@/stores/modalStore'

import { markPushPrompted } from '../../api/notificationsApi'
import { usePushEnabled } from '../../hooks/useNotificationSettings'
import { useNotificationsUserId } from '../../hooks/useNotificationsUserId'
import { usePushSettings, useWebPushBrowserState } from '../../hooks/useWebPush'
import {
  forgetPushPrompted,
  PUSH_PROMPT_CHANNEL,
  pushPromptKey,
  rememberPushPrompted,
  wasPushPrompted
} from './promptMemory'

const PushPermissionModal = lazy(() =>
  import('../PushPermissionModal').then(module => ({
    default: module.PushPermissionModal
  }))
)

/** Mandatory steps and screens the offer must never cover. */
const EXCLUDED_PREFIXES = [
  '/join',
  '/auth',
  '/maintenance',
  '/clubs/register',
  '/r/'
]

const isExcluded = (pathname: string | null) =>
  !pathname ||
  EXCLUDED_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(prefix)
  )

// @/ui/Dialog flags <html> while any dialog is open.
const subscribeDialogs = (onChange: () => void) => {
  const observer = new MutationObserver(onChange)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-dialog-open']
  })
  return () => observer.disconnect()
}
const anyDialogOpen = () => 'dialogOpen' in document.documentElement.dataset

const subscribeLoad = (onChange: () => void) => {
  window.addEventListener('load', onChange)
  return () => window.removeEventListener('load', onChange)
}
const pageLoaded = () => document.readyState === 'complete'

type Readiness = 'wait' | 'offer' | 'never'

function useReadiness(): Readiness {
  const pathname = usePathname()
  const activeModal = useModalStore(state => state.activeModal)
  const dialogOpen = useSyncExternalStore(
    subscribeDialogs,
    anyDialogOpen,
    () => true
  )
  const loaded = useSyncExternalStore(subscribeLoad, pageLoaded, () => false)
  const browser = useWebPushBrowserState()
  const push = usePushEnabled()
  const settings = usePushSettings()

  // C8: the account's record wins over this browser's — shown elsewhere or
  // switched off by the fan means never. An older gateway sends neither.
  if (settings.data?.prompted_at || settings.data?.disabled_by_user) {
    return 'never'
  }

  const ready =
    loaded &&
    !dialogOpen &&
    activeModal === null &&
    !isExcluded(pathname) &&
    !browser.isLoading &&
    (browser.status === 'default' || browser.status === 'not-subscribed') &&
    !push.isLoading &&
    !push.isError &&
    !push.enabled &&
    settings.isSuccess &&
    (settings.data?.subscriptions?.length ?? 0) === 0

  return ready ? 'offer' : 'wait'
}

function OfferWatcher({
  onReady,
  onNever
}: {
  onReady: () => void
  onNever: () => void
}) {
  const readiness = useReadiness()

  useEffect(() => {
    if (readiness === 'offer') {
      onReady()
    } else if (readiness === 'never') {
      onNever()
    }
  }, [readiness, onReady, onNever])

  return null
}

/**
 * Takes the offer for this tab. With Web Locks the check-and-set is atomic
 * across tabs; without them it is best effort, backed by the storage event
 * and the broadcast the other tabs listen to.
 */
async function claimOffer(userId: string): Promise<boolean> {
  const claim = () => {
    if (wasPushPrompted(userId)) {
      return false
    }
    rememberPushPrompted(userId)
    return true
  }

  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  if (!locks?.request) {
    return claim()
  }

  return locks.request(
    `${PUSH_PROMPT_CHANNEL}:${userId}`,
    { ifAvailable: true },
    lock => (lock ? claim() : false)
  )
}

/** Hears another tab of the same account showing the offer. */
function useOtherTabShowedOffer(userId: string, onShown: () => void) {
  useEffect(() => {
    const key = pushPromptKey(userId)
    const handleStorage = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== null) {
        onShown()
      }
    }
    window.addEventListener('storage', handleStorage)

    const channel =
      typeof BroadcastChannel === 'undefined'
        ? undefined
        : new BroadcastChannel(PUSH_PROMPT_CHANNEL)
    const handleMessage = (event: MessageEvent<{ userId?: string }>) => {
      if (event.data?.userId === userId) {
        onShown()
      }
    }
    channel?.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('storage', handleStorage)
      channel?.removeEventListener('message', handleMessage)
      channel?.close()
    }
  }, [userId, onShown])
}

/** Commits together with the modal: runs once the chunk loaded and rendered. */
function OnShown({ run }: { run: () => void }) {
  useEffect(run, [run])
  return null
}

/** A modal that cannot load must not take the page down with it. */
class OfferBoundary extends Component<
  { onError: () => void; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch() {
    this.props.onError()
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

function AccountPrompt({ userId }: { userId: string }) {
  const [phase, setPhase] = useState<'waiting' | 'open' | 'done'>(() =>
    wasPushPrompted(userId) || isClubUserSession() ? 'done' : 'waiting'
  )
  const claiming = useRef(false)
  const reported = useRef(false)

  const standDown = useCallback(
    () => setPhase(current => (current === 'waiting' ? 'done' : current)),
    []
  )
  useOtherTabShowedOffer(userId, standDown)

  const show = useCallback(() => {
    if (claiming.current) {
      return
    }
    claiming.current = true
    void claimOffer(userId).then(claimed => {
      if (!claimed) {
        setPhase('done')
        return
      }
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(PUSH_PROMPT_CHANNEL)
        channel.postMessage({ userId })
        channel.close()
      }
      setPhase('open')
    })
  }, [userId])

  const skip = useCallback(() => {
    rememberPushPrompted(userId)
    setPhase('done')
  }, [userId])

  // C8: the server hears about it once the offer is really on screen.
  const reportShown = useCallback(() => {
    if (reported.current) {
      return
    }
    reported.current = true
    // Best effort: a failure only means another browser may offer it again.
    void markPushPrompted().catch(() => undefined)
  }, [])

  const fail = useCallback(() => {
    forgetPushPrompted(userId)
    setPhase('done')
  }, [userId])

  if (phase === 'done') {
    return null
  }

  if (phase === 'waiting') {
    return <OfferWatcher onReady={show} onNever={skip} />
  }

  return (
    <OfferBoundary onError={fail}>
      <Suspense fallback={null}>
        <PushPermissionModal open onClose={() => setPhase('done')} />
        <OnShown run={reportShown} />
      </Suspense>
    </OfferBoundary>
  )
}

/**
 * BAT-980: Arena's offer to turn on Web Push, shown once per account (the
 * server's `prompted_at`, cached in localStorage; one tab of several claims
 * it) to a signed-in fan after the main screen has loaded — not over sign-up, e-mail confirmation or
 * any other dialog, not to guests or clubs, not when the browser blocks or
 * cannot do Web Push, and not when push is already on or was set up before.
 * Mounted once in the locale layout.
 */
export function PushPermissionPrompt() {
  const userId = useNotificationsUserId()

  return userId ? <AccountPrompt key={userId} userId={userId} /> : null
}
